import type { ReactElement } from "react";
import { createUaOfferingPlugin } from "@constructorfleet/ua-offering";
import uaOfferingCatalog, { CO_UA_OFFERING_ID } from "./offering-catalog";
import { uaNotificationsPublicApiEndpoints } from "./public-api-endpoints";
import { UaAdminPage, UaDashboardPage, UaProductPage, UaDemoDashboardPage } from "./pages";
import { UaSignup } from "./ua-signup";
import { DEFAULT_UA_ADMIN_SETTINGS, isUaCoverageStatus, type UaAdminSettings } from "./settings";

type UaProductDbClient = {
  requestAdmin?: <TValue = unknown>(input: {
    method: "GET" | "POST" | "DELETE";
    path: string;
    body?: unknown;
  }) => Promise<{ ok: true; data: TValue } | { ok: false; error: string }>;
};

export type UaSettingsSaveResult = {
  ok: true;
};

/** @deprecated Use UaSettingsSaveResult instead */
export type CoUaSettingsSaveResult = UaSettingsSaveResult;

export async function saveUaAdminSettings(
  settings: UaAdminSettings,
  dbClient: UaProductDbClient
): Promise<UaSettingsSaveResult> {
  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/co-ua/settings",
      body: settings,
    });
    if (!response.ok) {
      throw new Error(response.error);
    }
  }

  return {
    ok: true,
  };
}

/** @deprecated Use saveUaAdminSettings instead */
export const saveCoUaAdminSettings = saveUaAdminSettings;

export async function loadUaAdminSettings(dbClient: UaProductDbClient): Promise<UaAdminSettings> {
  if (!dbClient.requestAdmin) {
    return DEFAULT_UA_ADMIN_SETTINGS;
  }

  const response = await dbClient.requestAdmin<{ settings?: unknown }>({
    method: "GET",
    path: "/api/admin/co-ua/settings",
  });

  const settings = response.ok ? response.data.settings : null;
  if (
    !settings ||
    typeof settings !== "object" ||
    !("coverageStatus" in settings) ||
    !isUaCoverageStatus(settings.coverageStatus)
  ) {
    return DEFAULT_UA_ADMIN_SETTINGS;
  }

  return {
    coverageStatus: settings.coverageStatus as UaAdminSettings["coverageStatus"],
  };
}

/** @deprecated Use loadUaAdminSettings instead */
export const loadCoUaAdminSettings = loadUaAdminSettings;

export const uaOfferingPlugin = createUaOfferingPlugin<
  UaProductDbClient,
  UaAdminSettings,
  UaSettingsSaveResult,
  ReactElement
>({
  catalog: uaOfferingCatalog,
  publicApiEndpoints: uaNotificationsPublicApiEndpoints,
  loadSettings: loadUaAdminSettings,
  saveSettings: saveUaAdminSettings,
  adminUI: (dbClient) => (
    <UaAdminPage dbClient={dbClient} isAdmin={Boolean((dbClient as any)?.isAdmin)} />
  ),
  productPage: () => <UaProductPage />,
  dashboardPage: () => <UaDashboardPage />,
  signupPage: () => <UaSignup />,
  demoPage: () => <UaDemoDashboardPage />,
});

/** @deprecated Use uaOfferingPlugin instead */
export const coUaOfferingPlugin = uaOfferingPlugin;

export default uaOfferingPlugin;
